import type { NativeSelectOption } from '@shared/ui/fields'
import type { DisciplineBookingsInfo, DisciplineDetails, DisciplineGeneralForm } from './interfaces'
import type { DisciplineUpdatePayload } from './types'
import type { AppRequestRoute } from '@shared/config'

import { Stage } from '@brighterly/lib-core-types/discipline/enums'
import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

/** Stages an agent may only reach by moving the card on the kanban board. The backend accepts them
 *  from the General info form too: the restriction is the legacy page's, kept on purpose. */
const KANBAN_ONLY_STAGES: readonly string[] = [
  Stage.BillingIssues,
  Stage.BillingOnHold,
  Stage.Canceled,
  Stage.Refund,
  Stage.Dispute,
  Stage.InProgress,
  Stage.Negotiations,
  Stage.ClosedLost,
  Stage.WaitingList,
  Stage.NewBillingRequests,
]

/** Mirrors `DisciplineStage::upgrade()` and `::billing()` — the board a discipline's card sits on. */
const UPGRADE_STAGES: readonly string[] = [
  Stage.ContactNeeded,
  Stage.InProgress,
  Stage.Negotiations,
  Stage.Sleeping,
  Stage.WaitingForPayment,
  Stage.ClosedWon,
  Stage.ClosedLost,
  Stage.WaitingList,
]

const BILLING_STAGES: readonly string[] = [
  Stage.NewBillingRequests,
  Stage.UpgradeDowngrade,
  Stage.FailPayment,
  Stage.BillingIssues,
  Stage.BillingOnHold,
  'NEW_PURCHASE',
  Stage.Canceled,
  Stage.Refund,
  Stage.Dispute,
]

const KANBAN_ONLY_SUFFIX = '(use only in kanban)'

export const buildStageOptions = (
  labels: Record<string, string>,
  selected: string | null,
  hasTutor: boolean,
): NativeSelectOption[] => Object.entries(labels).map(([value, text]) => {
  const isKanbanOnly = KANBAN_ONLY_STAGES.includes(value)
  const isAutoActivation = hasTutor && value === Stage.Activation

  return {
    id: value,
    value,
    text: isKanbanOnly ? `${text} ${KANBAN_ONLY_SUFFIX}` : text,
    selected: value === selected,
    disabled: isKanbanOnly || isAutoActivation,
  }
})

export const buildGradeOptions = (
  labels: Record<string, string>,
  selected: string | null,
): NativeSelectOption[] => Object.entries(labels).map(([value, text]) => ({
  id: value,
  value,
  text,
  selected: value === selected,
}))

export const buildDisciplineForm = (details: DisciplineDetails): DisciplineGeneralForm => ({
  notes: details.notes,
  stage: details.stage,
  curriculum_grade: details.curriculum_grade,
  manual_assign: details.manual_assign,
  additional_practice: details.additional_practice,
})

/** The API takes a partial body; sending only what the admin changed keeps a concurrent edit of
 *  another field (or a kanban move of the stage) from being overwritten with a stale value. */
export const buildDisciplineUpdatePayload = (
  details: DisciplineDetails,
  form: DisciplineGeneralForm,
): DisciplineUpdatePayload => {
  const initial = buildDisciplineForm(details)
  const payload: DisciplineUpdatePayload = {}
  const keys = Object.keys(form) as (keyof DisciplineGeneralForm)[]

  keys.forEach((key) => {
    const value = key === 'notes' ? (form.notes || null) : form[key]
    if (value !== initial[key]) Object.assign(payload, { [key]: value })
  })

  return payload
}

export const getSkippedLessonsCount = (info: DisciplineBookingsInfo): number =>
  info.no_show + info.canceled + info.rescheduled

export const formatLessonTime = (value: string | null): string =>
  value ? dayjs(value).format(DateFormat.KanbanDate) : ''

export const getKanbanRouteName = (stage: string | null): AppRequestRoute | null => {
  if (!stage) return null
  if (UPGRADE_STAGES.includes(stage)) return 'admin.kanban.upgrade'
  if (BILLING_STAGES.includes(stage)) return 'admin.kanban.billing'

  return 'admin.kanban.service'
}

export const getDisciplineName = (code: string, labels: Record<string, string> | undefined): string =>
  labels?.[code] ?? code
